import type { VersionedDraftKit } from '@/lib/versioned-draft';
import { canSaveVersion } from '@/lib/versioned-draft';
import type {
  InvolvementLevel,
  PersonaContent,
  PersonaDetail,
  PersonaDraftContent,
  PersonaInvolvement,
  PersonaPresentationKind,
  PersonaTone,
} from '@matter_extractor/shared';

/**
 * What the Personas editor holds while somebody types (refonte modulaire §10).
 *
 * The comparison that greys « Enregistrer », the version numbering and the
 * three refusals are in `versioned-draft.ts` and hold for every versioned
 * object. What is here is the SHAPE of a persona's draft, the four functions
 * that cross between it and the wire, and the ONE rule that makes a persona
 * different from a criterion: the involvement is RESOLVED against the current
 * scale on its way out.
 *
 * ## Why the kit is a FACTORY and not a constant
 *
 * `CRITERION_DRAFT_KIT` is a constant because everything a criterion's version
 * needs is in the draft. A persona's is not: §10.1 says that with `overridden`
 * false, « la direction recopiée est la direction courante du cran AU MOMENT DE
 * L'ENREGISTREMENT », so a persona nobody redefined follows the last rewriting
 * of its cran at each new version. The scale is the second half of that
 * sentence, and the draft cannot hold it - a cran rewritten in Réglages after
 * this editor opened must still reach the version.
 *
 * So the kit is built from the scale the editor already had to read for its
 * slider, and stays PURE: `personaDraftKit(levels)` closes over a list, and
 * every function below is a function of its two inputs. The alternative -
 * resolving inside the form on each render - would be a write into state from
 * a render, which is exactly the effect this repo bans.
 *
 * ## The draft's presentation is FLAT, and that is §10.1
 *
 * « Repasser en anonyme les cache sans les effacer du brouillon, pour qu'un
 * aller-retour ne coûte pas une ressaisie. » Under the discriminated union a
 * version uses, an anonymous draft has nowhere to keep the first name, so a
 * reload - the very thing the server draft exists to survive - would eat what
 * was typed. `personaDraftPresentationSchema` says the same thing on the wire.
 */

/** The presentation while somebody types: both fields kept, whichever is shown. */
export interface PersonaDraftPresentationFields {
  readonly kind: PersonaPresentationKind;
  /** Kept while anonymous hides it (§10.1), which is why it is not optional here. */
  readonly firstName: string;
  /** The self-introduction direction, kept the same way. */
  readonly direction: string;
}

/**
 * The involvement while somebody types.
 *
 * `name` and `direction` are the copy FROZEN in the version this draft came
 * from, and they are kept for exactly one state: a cran RETIRED from the scale,
 * where there is no current wording to fall back to and §10.1 keeps the text
 * (« cran retiré du réglage, texte conservé »). While the cran still exists and
 * `overridden` is false, both are replaced by the scale's own on the way out.
 *
 * When `overridden` is true, `direction` is what the operator redefined and it
 * is what the version copies - rewriting the cran no longer touches it.
 */
export interface PersonaDraftInvolvement {
  readonly levelId: string;
  readonly name: string;
  readonly direction: string;
  readonly overridden: boolean;
}

export interface PersonaDraft {
  readonly name: string;
  readonly direction: string;
  readonly presentation: PersonaDraftPresentationFields;
  readonly involvement: PersonaDraftInvolvement;
  /** A COPY of a library tone, or none: « les deux ensemble ou rien » (§10.1). */
  readonly tone: PersonaTone | null;
  /** « Son histoire » on screen, `background` in code. Legitimately empty. */
  readonly background: string;
}

const EMPTY_PRESENTATION: PersonaDraftPresentationFields = {
  kind: 'anonymous',
  firstName: '',
  direction: '',
};

/** The cran a NEW persona opens on: the first of the scale (H14). */
function firstLevel(levels: readonly InvolvementLevel[]): PersonaDraftInvolvement {
  const level = levels[0];
  return {
    levelId: level?.id ?? '',
    name: level?.name ?? '',
    direction: level?.direction ?? '',
    overridden: false,
  };
}

/**
 * The empty editor of a persona that does not exist yet (H14, §16).
 *
 * « Anonyme, premier cran, sans redéfinition » - the implication is never
 * empty, the slider always has a position, and the persona follows the cran's
 * wording until somebody redefines it.
 */
export function emptyPersonaDraft(levels: readonly InvolvementLevel[]): PersonaDraft {
  return {
    name: '',
    direction: '',
    presentation: EMPTY_PRESENTATION,
    involvement: firstLevel(levels),
    tone: null,
    background: '',
  };
}

/**
 * A draft from a stored content - a version's, or the server draft's.
 *
 * The parameter is `PersonaDraftContent`, the shared name for a partial
 * content, and not a locally derived `Partial<PersonaContent>`: the two are NOT
 * the same type under `exactOptionalPropertyTypes` - the local one promises a
 * present key holds a value, a draft off the wire carries `| undefined`.
 *
 * A stored involvement is taken WHOLE, `overridden` included. It is resolved
 * against the scale on the way OUT and never on the way in, so what is on
 * screen is what was stored - which is what makes « la direction du cran » in
 * the tooltip and « la direction redéfinie » in the field two different things
 * the operator can tell apart.
 */
export function personaDraftFromContent(
  content: PersonaDraftContent,
  levels: readonly InvolvementLevel[],
): PersonaDraft {
  const presentation = content.presentation;
  const involvement = content.involvement;
  return {
    name: content.name ?? '',
    direction: content.direction ?? '',
    presentation: {
      kind: presentation?.kind ?? 'anonymous',
      firstName: presentation?.firstName ?? '',
      direction: presentation?.direction ?? '',
    },
    involvement:
      involvement === undefined
        ? firstLevel(levels)
        : {
            levelId: involvement.levelId,
            name: involvement.name,
            direction: involvement.direction,
            overridden: involvement.overridden,
          },
    tone: content.tone ?? null,
    background: content.background ?? '',
  };
}

/**
 * What the editor opens on: the server draft when there is one, the
 * `développement` version otherwise, and the empty editor of H14 for a persona
 * that does not exist yet (§4, §10).
 */
export function openingPersonaDraft(
  detail: PersonaDetail | null,
  levels: readonly InvolvementLevel[],
): PersonaDraft {
  if (detail === null) return emptyPersonaDraft(levels);
  return personaDraftFromContent(detail.draft?.content ?? detail.developmentContent, levels);
}

/**
 * The involvement a save would write, resolved against the scale - the whole of
 * what `overridden` means (§10.1).
 *
 *   - **redefined** (`overridden` true): the direction is the operator's, and
 *     rewriting the cran does not touch it. The NAME still follows the cran
 *     while the cran exists, because a name is the cran's identity and not part
 *     of what was redefined.
 *   - **following** (`overridden` false) with the cran still in the scale: BOTH
 *     are recopied from the scale as it stands right now, which is the sentence
 *     « suit la dernière rédaction du cran à chaque nouvelle version ».
 *   - **following** a cran RETIRED from the scale: the frozen copy is kept, and
 *     nothing is invalidated (§10.1). There is nowhere else to read a wording.
 *
 * `null` when the result could not be a version: an empty redefinition, or a
 * retired cran whose frozen text is somehow empty. H15 does not list this among
 * what greys the button, and it does not have to - a redefinition is opened
 * pre-filled, so an empty one is a field the operator cleared on purpose, and
 * a button that stayed armed would produce a 400 on the click instead.
 */
export function resolvedInvolvement(
  draft: PersonaDraftInvolvement,
  levels: readonly InvolvementLevel[],
): PersonaInvolvement | null {
  const level = levels.find((one) => one.id === draft.levelId) ?? null;
  const name = (level?.name ?? draft.name).trim();
  const direction = (
    draft.overridden ? draft.direction : (level?.direction ?? draft.direction)
  ).trim();
  if (draft.levelId === '' || name === '' || direction === '') return null;
  return { levelId: draft.levelId, name, direction, overridden: draft.overridden };
}

/**
 * The content a draft would become, or `null` when it could not become one.
 *
 * `null` is the whole of H15: the name and the direction, plus the first name
 * and the self-introduction direction when the presentation is NAMED. The
 * history and the tone are not in the condition - both are optional (§10.1) -
 * and the involvement never is, because H14 gives a new persona a cran.
 *
 * Trimmed then emptied out, in the order `personaContentSchema` states: a field
 * holding three spaces is a direction an operator believes they wrote, and it
 * would reach the model as a blank line.
 */
export function normalizedPersonaContent(
  draft: PersonaDraft,
  levels: readonly InvolvementLevel[],
): PersonaContent | null {
  const name = draft.name.trim();
  const direction = draft.direction.trim();
  if (name === '' || direction === '') return null;

  const involvement = resolvedInvolvement(draft.involvement, levels);
  if (involvement === null) return null;

  const firstName = draft.presentation.firstName.trim();
  const selfDirection = draft.presentation.direction.trim();
  if (draft.presentation.kind === 'named' && (firstName === '' || selfDirection === '')) {
    return null;
  }

  return {
    name,
    direction,
    presentation:
      draft.presentation.kind === 'named'
        ? { kind: 'named', firstName, direction: selfDirection }
        : { kind: 'anonymous' },
    involvement,
    tone: normalizedTone(draft.tone),
    background: draft.background.trim(),
  };
}

/** « Les deux ensemble ou rien » (§10.1): a half-filled tone is no tone. */
function normalizedTone(tone: PersonaTone | null): PersonaTone | null {
  if (tone === null) return null;
  const name = tone.name.trim();
  const text = tone.text.trim();
  return name === '' || text === '' ? null : { name, text };
}

/**
 * What an autosave SENDS.
 *
 * `personaSaveDraftSchema` parses `personaContentSchema.partial()`, and
 * `.partial()` makes each field optional WITHOUT loosening it: `name` is still
 * `z.string().trim().min(1)` when it is there. So an empty name is OMITTED and
 * never sent as `''` - a draft of a persona nobody has named yet would
 * otherwise be answered with a 400, which is the one refusal an autosave must
 * never produce: it fires while somebody types, and a red box on the third
 * keystroke of an empty field teaches an operator that the editor is broken.
 *
 * `presentation`, `tone` and `background` are sent even when empty, because
 * emptying them IS a change the draft must record: `background` bottoms out at
 * zero in the schema, `tone` is nullable, and the presentation carries the
 * `kind` that decides which fields the editor shows. Its two texts follow the
 * name's rule and are omitted when blank - they are `min(1)` too.
 */
export function personaDraftPayload(
  draft: PersonaDraft,
  levels: readonly InvolvementLevel[],
): PersonaDraftContent {
  const firstName = draft.presentation.firstName.trim();
  const selfDirection = draft.presentation.direction.trim();

  const payload: PersonaDraftContent = {
    presentation: {
      kind: draft.presentation.kind,
      ...(firstName === '' ? {} : { firstName }),
      ...(selfDirection === '' ? {} : { direction: selfDirection }),
    },
    tone: normalizedTone(draft.tone),
    background: draft.background.trim(),
  };

  const name = draft.name.trim();
  if (name !== '') payload.name = name;
  const direction = draft.direction.trim();
  if (direction !== '') payload.direction = direction;
  const involvement = resolvedInvolvement(draft.involvement, levels);
  if (involvement !== null) payload.involvement = involvement;

  return payload;
}

/** Everything the versioned editor needs to run a persona's draft (§4). */
export function personaDraftKit(
  levels: readonly InvolvementLevel[],
): VersionedDraftKit<PersonaContent, PersonaDraftContent, PersonaDraft> {
  return {
    opening: (detail) => openingPersonaDraft(detail, levels),
    fromContent: (content) => personaDraftFromContent(content, levels),
    normalized: (draft) => normalizedPersonaContent(draft, levels),
    payload: (draft) => personaDraftPayload(draft, levels),
  };
}

/** Is « Enregistrer une nouvelle version » usable? The three refusals of §4, plus H15. */
export function canSavePersona(
  draft: PersonaDraft,
  detail: PersonaDetail | null,
  levels: readonly InvolvementLevel[],
): boolean {
  return canSaveVersion(draft, detail, personaDraftKit(levels));
}
